import { identity, pluck } from 'underscore';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DefaultButton, ChoiceGroup, Text, Stack } from '@fluentui/react';
import { withScope } from '@bingads-webui-react/scope-service';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getRoutePathById, withCampaignSiteMap } from '@bingads-webui-react/with-site-map';
import { PanelSelector, useTreeViewPanelSelector, fillDataToBulkAddedItem, useAPIsOfListingGroup } from '@bingads-webui-campaign-react/listing-groups-page';
import { SelectedItem } from '@bingads-webui-campaign-react/pill-item-components';
import { withStoreFromProviderId } from '@bingads-webui-campaign-react/with-store-details';
import { useDateRange } from '@bingads-webui-campaign-react/wizard-shared-components';
import { siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { HelpPopup } from '@bingads-webui-react/help-popup';

export const getStyles = (_, { palette = {} }) => ({
  productOptions: {
    '& label': {
      padding: 0,
      fontWeight: 'normal',
    },
  },
  buttonContainer: {
    marginTop: 10,
    '& .iconba': {
      fontSize: '16px',
      color: palette.themePrimary,
      marginRight: '8px',
      paddingBottom: '5px', // align to link
    },
  },
  merchantCenterAccountContainer: {
    marginBottom: 12,
  },
  productFilterEditContainer: {
    marginBottom: 12,
  },
});

const stackContainerToken = {
  childrenGap: 8,
};

const ListingGroupsWithCustomValidation = (props) => {
  const {
    scope, campaignId, i18n, updateValidation, campaignSettings, siteMap, listingGroup, storeDetails, assetGroupId, onClickNavigateToListingGroups,
  } = props;
  const classes = useDefaultStyles(getStyles);
  const {
    isTreeViewSelectorOpen,
    setIsTreeViewSelectorOpen,
    treeViewSelectorColumns,
    treeViewSelectorDimensions,
    treeViewSelectorInitialSelectedItems,
    treeViewSelectorItems,
    treeViewSelectorHeaderText,
    onTreeViewCancel,
    onTreeViewSelectedDimensionChangeWithoutStatistics,
    isTreeViewShimmerEnabled,
    onFilterProduct,
  } = useTreeViewPanelSelector({
    i18n,
    setAllRowRawData: identity,
    scope,
    currentCampaign: {
      Id: campaignId,
      CampaignSettings: [campaignSettings],
    },
    refreshGridData: identity,
    getArguemntsOfUpdateGridDataByCurrentState: identity,
  });
  const { listingGroupGridData } = useAPIsOfListingGroup();
  const options = useMemo(() => [
    {
      key: 'all-products',
      text: i18n.getString(_TL_('All products')),
    },
    {
      key: 'filter-products',
      text: (
        <div>
          {i18n.getString(_TL_('Filtered products'))}
          <HelpPopup
            aria-label="Filtered products"
            topic="pop_BA_ProdAdAddFilter"
          />
        </div >
      ),
    },
  ], [i18n]);
  const isEditing = useMemo(() => !!scope.values.assetGroupId || assetGroupId, [assetGroupId, scope.values.assetGroupId]);
  const [selectedOption, setSelectedOption] = useState(listingGroup && listingGroup.items && listingGroup.items.length > 0 ? options[1] : options[0]);
  const [selectedProducts, setSelectedProducts] = useState(listingGroup && listingGroup.items);
  const [selectedOperand, setSelectedOperand] = useState(listingGroup && listingGroup.operand);
  const [productFilter, setProductFilter] = useState(null);

  const onChange = useCallback((newData, selectedDimension) => {
    newData.forEach((selectedItem) => {
      fillDataToBulkAddedItem(selectedItem);
    });
    setSelectedProducts(newData);
    setSelectedOperand(selectedDimension);
    setIsTreeViewSelectorOpen(false);
    updateValidation([], {
      listingGroup: {
        items: newData,
        operand: selectedDimension.data.operand,
      },
    });
  }, [setIsTreeViewSelectorOpen, updateValidation]);
  const onOpenPanel = useCallback(() => {
    onFilterProduct();
  }, [onFilterProduct]);
  const onDeleteProduct = useCallback((index) => {
    selectedProducts.splice(index, 1);
    setSelectedProducts([...selectedProducts]);
    updateValidation([], {
      listingGroup: {
        items: [...selectedProducts],
        operand: selectedOperand.data.operand,
      },
    });
  }, [selectedOperand, selectedProducts, updateValidation]);
  const navigateToListingGroupPage = useCallback(() => {
    if (onClickNavigateToListingGroups) {
      onClickNavigateToListingGroups();
    } else {
      scope.save(getRoutePathById(siteMap, 'listing-groups'));
    }
  }, [onClickNavigateToListingGroups, scope, siteMap]);
  const onChangeOptions = useCallback((_, option) => {
    setSelectedOption(option);

    const hasSelectedProducts = selectedProducts && selectedProducts.length > 0;
    const hasSelectedOperand = selectedOperand && selectedOperand.data && selectedOperand.data.operand;

    const updatedListingGroup = option.key === 'all-products' || !hasSelectedProducts || !hasSelectedOperand ?
      null : { items: selectedProducts, operand: selectedOperand.data.operand };

    updateValidation([], { listingGroup: updatedListingGroup });
  }, [selectedOperand, selectedProducts, updateValidation]);

  const dataRange = useDateRange();

  useEffect(() => {
    if (isEditing) {
      listingGroupGridData({
        queryOptions: {
          $top: 2,
        },
        entityPath: {
          accountId: scope.values.aid,
          campaignId: scope.values.campaignId || campaignId,
          customerId: scope.values.cid,
          assetGroupId: scope.values.assetGroupId || assetGroupId,
        },
        payload: {
          gridDataParameters: {
            DateRange: {
              StartDate: dataRange.StartDate,
              EndDate: dataRange.EndDate,
            },
          },
        },
      }).then(({ value }) => {
        if (value.length === 1) {
          setProductFilter(i18n.getString('ProductsGrid_Column_All'));
          updateValidation([], {
            productFilter: i18n.getString('ProductsGrid_Column_All'),
          });
        } else if (value.length === 2) {
          setProductFilter(i18n.getString(`ProductsGrid_Column_${value[1].Condition.Operand}`));
          updateValidation([], {
            productFilter: i18n.getString(`ProductsGrid_Column_${value[1].Condition.Operand}`),
          });
        }
      });
    }
    // Remove the functions from dependency to reduce duplicate calls
  }, [scope.values.cid, scope.values.aid, scope.values.uid, dataRange.StartDate, dataRange.EndDate, scope.values.campaignId, scope.values.assetGroupId, isEditing, campaignId, assetGroupId]);

  return (
    <div>
      {storeDetails && storeDetails.text &&
        <div className={classNames(classes.merchantCenterAccountContainer, 'store-details-container')}>
          <Text style={{ fontWeight: 'bold' }}>
            {i18n.getString(_TL_('Merchant Center account: '))}
          </Text>
          <Text className="store-text">
            {storeDetails.text}
          </Text>
        </div>
      }
      {isEditing && productFilter &&
        <div>
          <div className={classes.productFilterEditContainer}>
            <Text style={{ fontWeight: 'bold' }}>
              {i18n.getString(_TL_('Products:'))}
            </Text>
            <Text >
              {`${i18n.getString(_TL_(' Filtered by '))}${productFilter}`}
            </Text>
          </div>
          <div>
            <DefaultButton onClick={navigateToListingGroupPage}>
              {i18n.getString(_TL_('Go to listing groups'))}
            </DefaultButton>
          </div>
        </div>
      }
      {
        !isEditing &&
        <ChoiceGroup
          className={classNames(classes.productOptions, 'product-options')}
          options={options}
          onChange={onChangeOptions}
          selectedKey={selectedOption ? selectedOption.key : null}
          ariaLabel={i18n.getString(_TL_('Listing groups'))}
        />
      }
      {!isEditing && selectedOption.key === 'filter-products' &&
        <div>
          <div className={classNames('filtered-products-preview', classes.filterProductsPreview)}>
            <Stack
              horizontal
              wrap
              disableShrink
              tokens={stackContainerToken}
            >
              {pluck(selectedProducts, 'primary').map((item, index) => (
                <SelectedItem
                  key={item}
                  itemText={item}
                  itemIndex={index}
                  onDelete={onDeleteProduct}
                />
              ))}
            </Stack>
          </div>
          <div className={classes.buttonContainer}>
            <DefaultButton onClick={onOpenPanel}>
              <Glyphicon glyph="ba-Add" />
              {i18n.getString(_TL_('Add filter'))}
            </DefaultButton>
          </div>
        </div>
      }
      {isTreeViewSelectorOpen && <PanelSelector
        getErrors={() => []}// TO DO SIMXU update to real error handling
        isOpen={isTreeViewSelectorOpen}
        columns={treeViewSelectorColumns}
        dimensions={treeViewSelectorDimensions}
        headerText={treeViewSelectorHeaderText}
        initialSelectedItems={treeViewSelectorInitialSelectedItems}
        items={treeViewSelectorItems}
        onCancel={onTreeViewCancel}
        onSave={onChange}
        onSelectedDimensionChange={onTreeViewSelectedDimensionChangeWithoutStatistics}
        enableShimmer={isTreeViewShimmerEnabled}
        keyOfItem={item => item.key}
        isBulkAddEnabledForDimension={d => d.data.isBulkAddEnabled}
      />}
    </div>
  );
};

ListingGroupsWithCustomValidation.propTypes = {
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      locationTargets: PropTypes.shape({}),
    }),
  }).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
      uid: PropTypes.number.isRequired,
      campaignId: PropTypes.number.isRequired,
      assetGroupId: PropTypes.number,
    }).isRequired,
    save: PropTypes.func.isRequired,
  }).isRequired,
  campaignId: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  campaignSettings: PropTypes.arrayOf(PropTypes.shape({})),
  siteMap: siteMapPropType.isRequired,
  listingGroup: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
    })),
    operand: PropTypes.string,
  }),
  storeDetails: PropTypes.shape({
    text: PropTypes.string,
  }),
  assetGroupId: PropTypes.number,
  onClickNavigateToListingGroups: PropTypes.func,
};

ListingGroupsWithCustomValidation.defaultProps = {
  campaignSettings: [],
  listingGroup: {},
  storeDetails: undefined,
  assetGroupId: undefined,
  onClickNavigateToListingGroups: undefined,
};

export const ValidatedListingGroups = withCampaignSiteMap(withScope(withCustomValidation(withStoreFromProviderId(ListingGroupsWithCustomValidation))));
